"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { getOrganizationTeams, Team } from '@/utils/organizations/getOrganizationTeams';
import { getLocationsByOrganization, GymLocation } from '@/utils/services/gymLocationsService';
import { isCompletedSeason } from '@/utils/teams/teamSeasonStatus';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import SearchField from '@/components/form/fields/SearchField';
import BrandedSpinner from '@/components/common/BrandedSpinner';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('TeamSelector');

interface TeamSelectorProps {
  organizationId: string;
  selectedTeamIds: string[];
  onChange: (teamIds: string[]) => void;
  /** Optional filter for active only teams. Defaults to true. */
  activeOnly?: boolean;
  /** Optional season ID to filter teams. */
  seasonId?: string;
  /**
   * Opt-in: when true, teams from completed/ended seasons (including teams whose
   * season was archived early) are hidden from the picker. Defaults to false so
   * existing callers keep their current behavior. Reuses the shared
   * isCompletedSeason predicate via the getOrganizationTeams `includeSeason` join.
   * (activeOnly already excludes inactive/archived teams, so a completed season
   * is the only remaining historical condition — same approach as Step5Teams.)
   */
  excludeHistoricalSeasons?: boolean;
  /** Optional program ID to filter teams. */
  programId?: string;
  /** Whether to show the location filter tabs. Defaults to true. */
  showLocationFilter?: boolean;
  /** Whether to show the search field. Defaults to true. */
  showSearch?: boolean;
  /** Optional custom class name for the container. */
  className?: string;
  /** Optional title for the selection area. */
  title?: string;
  /** Optional callback when teams are loaded, so parent can access team data. */
  onTeamsLoaded?: (teams: Team[]) => void;
  /**
   * Coach comms unification (U5): display-only restriction to a fixed team-id
   * set (e.g. a coach's assigned teams). Server-side enforcement lives in the
   * communication scope validator — this just keeps the picker honest.
   */
  restrictToTeamIds?: string[];
}

const TeamSelector: React.FC<TeamSelectorProps> = ({
  organizationId,
  selectedTeamIds,
  onChange,
  activeOnly = true,
  seasonId,
  excludeHistoricalSeasons = false,
  programId,
  showLocationFilter = true,
  showSearch = true,
  className = "",
  title = "Select Teams",
  onTeamsLoaded,
  restrictToTeamIds
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [locations, setLocations] = useState<GymLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLocationId, setActiveLocationId] = useState<'all' | string>('all');

  useEffect(() => {
    if (!organizationId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const [teamsData, locationsData] = await Promise.all([
          getOrganizationTeams(organizationId, {
            activeOnly,
            seasonId,
            programId,
            // When hiding historical-season teams we need each team's season so
            // the shared isCompletedSeason predicate can evaluate it.
            includeSeason: excludeHistoricalSeasons,
          }),
          getLocationsByOrganization(organizationId)
        ]);

        let loadedTeams = excludeHistoricalSeasons
          ? (teamsData as Team[]).filter(team => !isCompletedSeason(team.season))
          : (teamsData as Team[]);
        if (restrictToTeamIds) {
          const allowed = new Set(restrictToTeamIds);
          loadedTeams = loadedTeams.filter(team => allowed.has(team.id));
        }

        setTeams(loadedTeams);
        setLocations(locationsData);
        onTeamsLoaded?.(loadedTeams);
      } catch (error) {
        logger.error('Error loading teams and locations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [organizationId, activeOnly, seasonId, programId, excludeHistoricalSeasons, restrictToTeamIds?.join(',')]);

  const filteredTeams = useMemo(() => {
    return teams.filter(team => {
      const matchesSearch = searchTerm === '' || 
        team.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesLocation = activeLocationId === 'all' || 
        team.location_id === activeLocationId;
      
      return matchesSearch && matchesLocation;
    });
  }, [teams, searchTerm, activeLocationId]);

  const locationCounts = useMemo(() => {
    const counts: Record<string, number> = { all: teams.length };
    locations.forEach(loc => {
      counts[loc.id] = teams.filter(t => t.location_id === loc.id).length;
    });
    return counts;
  }, [teams, locations]);

  const handleToggleTeam = (teamId: string) => {
    const newSelected = selectedTeamIds.includes(teamId)
      ? selectedTeamIds.filter(id => id !== teamId)
      : [...selectedTeamIds, teamId];
    onChange(newSelected);
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredTeams.map(t => t.id);
    const newSelected = Array.from(new Set([...selectedTeamIds, ...filteredIds]));
    onChange(newSelected);
  };

  const handleClearAllSelected = () => {
    onChange([]);
  };

  const getLocationName = (locationId?: string) => {
    if (!locationId) return 'No Location';
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : 'Unknown Location';
  };

  if (isLoading && teams.length === 0) {
    return (
      <div className="py-8 text-center">
        <BrandedSpinner size="sm" className="mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Loading teams...</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header and Actions */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSelectAllFiltered}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Select All Filtered
          </button>
          <span className="text-gray-300 dark:text-gray-700">|</span>
          <button
            type="button"
            onClick={handleClearAllSelected}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Search */}
      {showSearch && (
        <SearchField
          id="team-selector-search"
          name="teamSearch"
          placeholder="Search teams by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          showClearButton={true}
        />
      )}

      {/* Location Filters */}
      {showLocationFilter && locations.length > 0 && (
        <div className="flex flex-wrap gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            type="button"
            onClick={() => setActiveLocationId('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center whitespace-nowrap transition-all ${
              activeLocationId === 'all'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 border border-blue-200 dark:border-blue-800'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            All
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 shadow-sm">
              {locationCounts.all}
            </span>
          </button>
          {locations.map(location => (
            <button
              key={location.id}
              type="button"
              onClick={() => setActiveLocationId(location.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center whitespace-nowrap transition-all ${
                activeLocationId === location.id
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 border border-blue-200 dark:border-blue-800'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {location.name}
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 shadow-sm">
                {locationCounts[location.id] || 0}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Teams List */}
      <div className="card-surface rounded-xl overflow-hidden">
        {filteredTeams.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {filteredTeams.map((team) => {
              const isSelected = selectedTeamIds.includes(team.id);
              return (
                <div 
                  key={team.id} 
                  className={`flex items-center justify-between p-4 transition-all hover:bg-gray-50 dark:hover:bg-white/[0.02] ${
                    isSelected ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white truncate">
                        {team.name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                      <span className="truncate">{getLocationName(team.location_id)}</span>
                      {team.level && (
                        <>
                          <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                          <span>Level {team.level}{team.age_division && <span className="capitalize"> {team.age_division}</span>}</span>
                        </>
                      )}
                      {!team.level && team.age_division && (
                        <>
                          <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                          <span className="capitalize">{team.age_division}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggleTeam(team.id)}
                    className={`flex-shrink-0 px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                      isSelected 
                        ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-400/10 border border-red-100 dark:border-red-400/20 hover:bg-red-100 dark:hover:bg-red-400/20"
                        : "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/10 border border-blue-100 dark:border-blue-400/20 hover:bg-blue-100 dark:hover:bg-blue-400/20"
                    }`}
                  >
                    {isSelected ? (
                      <span className="flex items-center gap-1">
                        <GoogleMaterialIcon name="remove_circle" size="xs" />
                        Remove
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <GoogleMaterialIcon name="add_circle" size="xs" />
                        Add Team
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="bg-gray-100 dark:bg-gray-800 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <GoogleMaterialIcon name="groups" className="text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm ? 'No teams match your search.' : 'No teams found.'}
            </p>
          </div>
        )}
      </div>

      {/* Selection Stats */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <GoogleMaterialIcon name="check_circle" size="xs" className="text-blue-500" />
            <strong>{selectedTeamIds.length}</strong> teams selected
          </span>
          <span className="flex items-center gap-1">
            <GoogleMaterialIcon name="visibility" size="xs" className="text-gray-400" />
            <strong>{filteredTeams.length}</strong> visible
          </span>
        </div>
        {selectedTeamIds.length > 0 && (
          <button 
            type="button"
            onClick={handleClearAllSelected}
            className="text-red-600 dark:text-red-400 hover:underline font-medium"
          >
            Clear selection
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamSelector;
